import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import heroImage from "../../assets/images/expertise/hero.png";
import innovation1 from "../../assets/images/expertise/innovation1.png";
import innovation2 from "../../assets/images/expertise/innovation2.png";

import home5 from "../../assets/images/home/home5.jpg";
import home6 from "../../assets/images/home/home6.jpg";
import home7 from "../../assets/images/home/home7.jpg";

import { blogPosts } from "../../data/blogData";

const ExpertisePage = () => {
  const [activeTab, setActiveTab] = useState("comparison");
  const imageRef = useRef(null);
  const tabsRef = useRef([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const heroRef = useRef(null);
  const innovationRef = useRef(null);
  const signatureRef = useRef(null);
  const newsRef = useRef(null);
  const timelinesRef = useRef([]);

  const tabContent = {
    comparison: {
      image: home7,
      title: "Comparison Tools",
      description:
        "Easily assess the innovative approaches of leading architecture firms in one place.",
    },
    themes: {
      image: home6,
      title: "Firm Themes",
      description:
        "Explore various design philosophies and specialties that define each firm's unique identity.",
    },
    evaluation: {
      image: home5,
      title: "Evaluation Criteria",
      description:
        "Learn about the standards we use to rank and compare these top firms.",
    },
  };

  useEffect(() => {
    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Clear any existing animations
    timelinesRef.current.forEach((timeline) => timeline?.kill());
    timelinesRef.current = [];
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

    // Helper function for parallax effect
    const createParallax = (element) => {
      if (!element) return;

      gsap.to(element, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    };

    // Apply parallax to all images
    const images = document.querySelectorAll("img");
    images.forEach((img) => {
      img.style.transform = "scale(1.1)";
      createParallax(img);
    });

    // Ensure refs are available
    if (
      !heroRef.current ||
      !innovationRef.current ||
      !signatureRef.current ||
      !newsRef.current
    ) {
      return;
    }

    // Hero section animations
    const heroTimeline = gsap.timeline({
      paused: true,
      defaults: { ease: "power2.out" },
    });

    heroTimeline
      .fromTo(
        heroRef.current.querySelectorAll("h1, p"),
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
      )
      .fromTo(
        heroRef.current.querySelector("img"),
        { scale: 1.1, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1 },
        "-=0.4"
      );

    // Expertise section animations
    const expertiseTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: ".expertise-section",
        start: "top 80%",
        once: true,
        markers: false,
      },
      defaults: { ease: "power2.out" },
    });

    expertiseTimeline
      .fromTo(
        ".expertise-section h2, .expertise-section p",
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
      )
      .fromTo(
        ".expertise-section img",
        { scale: 1.1, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1 },
        "-=0.6"
      )
      .fromTo(
        ".expertise-section .grid-cols-3 > div",
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.2 },
        "-=0.4"
      );

    // Innovation section animations
    const innovationTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: innovationRef.current,
        start: "top 80%",
        once: true,
        markers: false,
      },
      defaults: { ease: "power2.out" },
    });

    innovationTimeline
      .fromTo(
        innovationRef.current.querySelectorAll("h2, p, h3"),
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
      )
      .fromTo(
        innovationRef.current.querySelector("img"),
        { x: 30, opacity: 0 },
        { x: 0, opacity: 1, duration: 1 },
        "-=0.8"
      );

    // Signature designs section animations
    const signatureTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: signatureRef.current,
        start: "top 80%",
        once: true,
        markers: false,
      },
      defaults: { ease: "power2.out" },
    });

    signatureTimeline
      .fromTo(
        signatureRef.current.querySelector("img"),
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, duration: 1 }
      )
      .fromTo(
        signatureRef.current.querySelectorAll("h2, p, h3"),
        { x: 30, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.8, stagger: 0.2 },
        "-=0.8"
      );

    // News section animations
    const newsTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: newsRef.current,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
        markers: false,
      },
      defaults: { ease: "power2.out" },
    });

    newsTimeline.fromTo(
      newsRef.current.querySelectorAll(".group"),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.2 }
    );

    // Store all timelines for cleanup
    timelinesRef.current = [
      heroTimeline,
      expertiseTimeline,
      innovationTimeline,
      signatureTimeline,
      newsTimeline,
    ];

    // Start hero animation immediately
    heroTimeline.play();

    // Cleanup function
    return () => {
      timelinesRef.current.forEach((timeline) => timeline?.kill());
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      ScrollTrigger.clearMatchMedia();
    };
  }, []); // Empty dependency array since we only want to run this once

  const handleTabChange = (newTab) => {
    if (isAnimating || newTab === activeTab) return;
    setIsAnimating(true);

    // Animate out current image
    gsap.to(imageRef.current, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.inOut",
      onComplete: () => {
        setActiveTab(newTab);
        // Animate in new image
        gsap.to(imageRef.current, {
          opacity: 1,
          duration: 0.5,
          ease: "power2.inOut",
          onComplete: () => setIsAnimating(false),
        });
      },
    });

    // Animate tab indicators
    tabsRef.current.forEach((tab, index) => {
      const isNewActive = Object.keys(tabContent)[index] === newTab;

      // Animate tab opacity
      gsap.to(tab, {
        opacity: isNewActive ? 1 : 0.5,
        duration: 0.3,
        ease: "power2.inOut",
      });

      // Animate indicator line
      gsap.to(tab.querySelector(".tab-indicator"), {
        scaleX: isNewActive ? 1 : 0,
        opacity: isNewActive ? 1 : 0,
        duration: 0.3,
        ease: "power2.inOut",
      });

      // Animate text
      gsap.to(tab.querySelector("h3"), {
        y: isNewActive ? 0 : 10,
        opacity: isNewActive ? 1 : 0.7,
        duration: 0.3,
        ease: "power2.out",
      });
    });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-8">
      {/* Hero Section */}
      <section ref={heroRef} className="my-16">
        <div className="grid grid-cols-2 gap-8 items-end mb-16">
          <h1 className="text-[40px] leading-[1.2] font-medium">
            Key Design Themes Shaping the Future of Architecture
          </h1>
          <p className="text-[15px] leading-relaxed text-gray-800 font-medium mt-2 font-serif">
            Discover the innovative design themes that are transforming our
            built environment. From sustainability to urban planning, these
            principles are at the forefront of modern architecture.
          </p>
        </div>
        <div className="w-full overflow-hidden">
          <img
            src={heroImage}
            alt="Modern architectural building with glass facade and wooden details"
            className="w-full h-[600px] object-cover bg-center bg-no-repeat"
          />
        </div>
      </section>

      {/* Second Hero Section - Repeated for design consistency */}
      <section className="my-32">
        <div className="grid grid-cols-2 gap-8 items-end mb-16">
          <h1 className="text-[40px] leading-[1.2] font-medium">
            Key Design Themes Shaping the Future of Architecture
          </h1>
          <p className="text-[15px] leading-relaxed text-gray-800 font-medium mt-2 font-serif">
            Discover the innovative design themes that are transforming our
            built environment. From sustainability to urban planning, these
            principles are at the forefront of modern architecture.
          </p>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="expertise-section my-32">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-4 mb-8">
            <h3 className="text-sky-400 text-base uppercase tracking-[0.2em] font-medium">
              EXPERTISE
            </h3>
            <div className="h-[1px] w-32 bg-sky-400"></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 items-end mb-16">
          <h2 className="text-[40px] leading-[1.2] font-medium">
            Evaluate and Compare Top Architecture Firms
          </h2>
          <p className="text-[15px] leading-relaxed text-gray-800 font-medium font-serif">
            Discover the unique themes and strengths of each firm. Use our
            interactive tools to make informed comparisons.
          </p>
        </div>

        <div className="relative mb-16 h-[600px] overflow-hidden">
          <img
            ref={imageRef}
            src={tabContent[activeTab].image}
            alt={tabContent[activeTab].title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="grid grid-cols-3 gap-8">
          {Object.entries(tabContent).map(([key, content], index) => (
            <div
              key={key}
              ref={(el) => (tabsRef.current[index] = el)}
              onClick={() => handleTabChange(key)}
              className={`text-center cursor-pointer transform transition-transform hover:scale-[1.02] ${
                activeTab === key ? "opacity-100" : "opacity-50"
              }`}
            >
              <div
                className={`tab-indicator h-[2px] bg-black mb-6 transform origin-left ${
                  activeTab === key ? "scale-x-100" : "scale-x-0"
                }`}
              />
              <h3 className="text-[20px] font-medium mb-3 transform">
                {content.title}
              </h3>
              <p className="text-[16px] text-gray-600 font-serif leading-relaxed">
                {content.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Innovation Section */}
      <section ref={innovationRef} className="my-32">
        <div className="grid grid-cols-2 gap-16">
          <div>
            <h2 className="text-[40px] leading-[1.2] font-medium mb-8">
              Innovative Design Meets Human Experience: Gensler and Foster's
              Vision
            </h2>
            <p className="text-[15px] leading-relaxed text-gray-800 font-medium font-serif mb-16">
              Discover how Gensler's Human Experience approach transforms spaces
              into vibrant environments. Explore Foster's Tech-Futurism, merging
              technology with architectural innovation for a sustainable future.
            </p>

            <div className="space-y-12">
              <div>
                <h3 className="text-[22px] font-medium mb-3">
                  Human Experience
                </h3>
                <p className="text-[15px] text-gray-800 font-serif leading-relaxed">
                  Gensler prioritizes user experience, creating spaces that
                  inspire and connect communities.
                </p>
              </div>
              <div>
                <h3 className="text-[22px] font-medium mb-3">Tech-Futurism</h3>
                <p className="text-[15px] text-gray-800 font-serif leading-relaxed">
                  Foster's designs integrate cutting-edge technology, paving the
                  way for future-ready architecture.
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-hidden">
            <img
              src={innovation1}
              alt="Team collaborating on architectural design"
              className="w-full h-full object-cover bg-center bg-no-repeat"
            />
          </div>
        </div>
      </section>

      {/* Signature Designs Section */}
      <section ref={signatureRef} className="my-32">
        <div className="grid grid-cols-2 gap-16">
          <div className="h-[500px] overflow-hidden">
            <img
              src={innovation2}
              alt="Architects working on design models"
              className="w-full h-full object-cover bg-center bg-no-repeat"
            />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-4 mb-8">
                <h3 className="text-sky-400 text-base uppercase tracking-[0.2em] font-medium">
                  Innovation
                </h3>
                <div className="h-[1px] w-32 bg-sky-400"></div>
              </div>
            </div>

            <h2 className="text-[40px] leading-[1.2] font-medium mb-8">
              Explore Signature Designs from Leading Firms
            </h2>

            <p className="text-[15px] leading-relaxed text-gray-800 font-medium font-serif">
              Discover the unique design philosophies that set top architecture
              firms apart. These signature approaches not only redefine
              aesthetics but also enhance functionality and sustainability.
            </p>
          </div>
        </div>
      </section>

      {/* News Section */}
      <div ref={newsRef} className="w-full py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-8">
              <h3 className="text-sky-400 text-base uppercase tracking-[0.2em] font-medium">
                NEWS
              </h3>
              <div className="h-[1px] w-32 bg-sky-400"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-medium">
              Latest News & Insights
            </h2>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {blogPosts.slice(0, 3).map((post, index) => (
              <Link to={`/blog/${post.slug}`} key={post.id} className="group">
                <div className="aspect-[4/3] overflow-hidden mb-6">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover bg-center bg-no-repeat"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">{post.category}</span>
                    <span className="text-gray-500">{post.date}</span>
                  </div>
                  <h3 className="text-xl font-medium group-hover:text-sky-400 transition-colors">
                    {post.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          {/* See More Button */}
          <div className="flex justify-end">
            <Link
              to="/blog"
              className="inline-flex items-center text-black border border-black px-8 py-3 hover:bg-black hover:text-white transition-colors duration-300 text-sm tracking-wider font-medium"
            >
              See More
              <svg className="ml-2 w-4 h-4" viewBox="0 0 24 24" fill="none">
                <path
                  d="M13.5 19L20.5 12L13.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.5 12L3.5 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertisePage;
